import React, { useState, useEffect } from 'react';
import { Clock, Radio, Cloud, Sun, Zap } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { qsoAPI } from '../services/api';
import Toast from '../components/Toast';

const S = {
  card: { backgroundColor:'white', borderRadius:10, border:'1px solid #E5E7EB', boxShadow:'0 1px 3px rgba(0,0,0,0.06)' },
  label: { fontSize:'0.6875rem', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em', color:'#9CA3AF' },
  badge: (color) => ({ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 8px', borderRadius:4, fontSize:'0.6875rem', fontWeight:600, letterSpacing:'0.03em', ...color }),
  btnPrimary: { padding:'7px 16px', borderRadius:6, backgroundColor:'#111827', color:'white', border:'none', fontWeight:600, fontSize:'0.8125rem', cursor:'pointer', letterSpacing:'0.01em' },
  btnOutline: { padding:'6px 14px', borderRadius:6, backgroundColor:'white', color:'#111827', border:'1px solid #D1D5DB', fontWeight:500, fontSize:'0.8125rem', cursor:'pointer' },
  btnGhost: { background:'none', border:'none', cursor:'pointer', color:'#6B7280', fontSize:'0.75rem', textDecoration:'underline', padding:0 },
};

const QSOPage = () => {
  const { isAuthenticated, user } = useAuth();
  const [qsoSessions, setQsoSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [proposeForm, setProposeForm] = useState(null);
  const [freeNom, setFreeNom] = useState('');
  const [freeIndicatif, setFreeIndicatif] = useState('');
  const [showMyPropositions, setShowMyPropositions] = useState(false);
  const [myPropositions, setMyPropositions] = useState([]);
  const [weather, setWeather] = useState(null);
  const [solar, setSolar] = useState(null);
  const [countdown, setCountdown] = useState(null);

  useEffect(() => { loadQSO(); loadWeather(); loadSolar(); }, []); // eslint-disable-line

  useEffect(() => {
    if (!isAuthenticated) return;
    qsoAPI.myProposals().then(r => setMyPropositions(r.data)).catch(() => {});
  }, [isAuthenticated]);

  useEffect(() => {
    document.body.style.overflow = proposeForm ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [proposeForm]);

  useEffect(() => {
    if (!qsoSessions.length) return;
    const target = qsoSessions[0];
    if (!target) return;
    const compute = () => {
      const [h, m] = (target.heure || '11:00').split(':').map(Number);
      const t = new Date(target.date);
      t.setHours(h, m, 0, 0);
      const diff = t - new Date();
      if (diff <= 0) { setCountdown({ label: "C'est maintenant !", live: true }); return; }
      const days = Math.floor(diff / 86400000);
      const hrs  = Math.floor((diff % 86400000) / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setCountdown({ days, hrs, mins, secs, live: false });
    };
    compute();
    const id = setInterval(compute, 1000);
    return () => clearInterval(id);
  }, [qsoSessions]);

  const loadQSO = async () => {
    try { const res = await qsoAPI.getAll(); setQsoSessions(res.data); }
    catch { showToast('Erreur de chargement', 'error'); }
    finally { setLoading(false); }
  };

  const loadWeather = async () => {
    try {
      const res = await fetch('https://wttr.in/Geneva?format=j1');
      const data = await res.json();
      const cur = data.current_condition[0];
      setWeather({
        temp: cur.temp_C,
        feels: cur.FeelsLikeC,
        desc: cur.lang_fr?.[0]?.value || cur.weatherDesc[0].value,
        humidity: cur.humidity,
        wind: cur.windspeedKmph,
        code: parseInt(cur.weatherCode),
      });
    } catch { }
  };

  const loadSolar = async () => {
    try {
      const res = await fetch('https://services.swpc.noaa.gov/products/noaa-planetary-k-index.json');
      const kData = await res.json();
      const lastK = parseFloat(kData[kData.length - 1][1]);
      const sfiRes = await fetch('https://services.swpc.noaa.gov/products/summary/10cm-flux.json');
      const sfiData = await sfiRes.json();
      setSolar({ k: lastK, sfi: sfiData.Flux });
    } catch { }
  };

  const weatherIcon = (code) => {
    if (code === 113) return '☀️';
    if ([116,119,122].includes(code)) return '⛅';
    if ([143,248,260].includes(code)) return '🌫️';
    if ([176,263,266,281,284,293,296,299,302,305,308,353,356,359].includes(code)) return '🌧️';
    if ([179,182,185,227,230,323,326,329,332,335,338,368,371,374,377].includes(code)) return '🌨️';
    if ([200,386,389,392,395].includes(code)) return '⛈️';
    return '🌤️';
  };

  const kColor = (k) => k <= 2 ? '#15803D' : k <= 4 ? '#D97706' : '#DC2626';
  const kLabel = (k) => k <= 2 ? 'Calme' : k <= 4 ? 'Modéré' : 'Perturbé';

  const propLevel = (k, sfi) => {
    if (k <= 2 && sfi >= 150) return { label: 'Excellente', color: '#15803D', bg: '#F0FDF4', border: '#BBF7D0', bars: 4 };
    if (k <= 2 && sfi >= 100) return { label: 'Bonne',      color: '#1D4ED8', bg: '#EFF6FF', border: '#BFDBFE', bars: 3 };
    if (k <= 4)               return { label: 'Faible',     color: '#D97706', bg: '#FFFBEB', border: '#FEF08A', bars: 2 };
    return                           { label: 'Perturbée',  color: '#DC2626', bg: '#FEF2F2', border: '#FECACA', bars: 1 };
  };

  const showToast = (msg, type = 'success') => setToast({ message: msg, type });

  const handlePropose = async (id, nomLibre, indicatifLibre) => {
    try {
      await qsoAPI.propose(id, nomLibre, indicatifLibre);
      showToast('Candidature transmise au comité.');
      setProposeForm(null); setFreeNom(''); setFreeIndicatif('');
      loadQSO();
      qsoAPI.myProposals().then(r => setMyPropositions(r.data)).catch(() => {});
    } catch (err) { showToast(err.response?.data?.error || 'Erreur', 'error'); }
  };

  const openForm = (id) => { setProposeForm(id); setFreeNom(''); setFreeIndicatif(''); };

  const handleCancel = async (id) => {
    try { await qsoAPI.cancel(id); showToast('Candidature annulée.'); loadQSO(); qsoAPI.myProposals().then(r => setMyPropositions(r.data)).catch(() => {}); }
    catch (err) { showToast(err.response?.data?.error || 'Erreur', 'error'); }
  };

  if (loading) return (
    <div style={{display:'flex',justifyContent:'center',alignItems:'center',minHeight:300}}>
      <div style={{width:28,height:28,border:'2px solid #111827',borderTopColor:'transparent',borderRadius:'50%',animation:'spin 0.8s linear infinite'}}/>
    </div>
  );

  const mainQSO = qsoSessions[0];
  const futureQSO = qsoSessions.slice(1, 6);

  return (
    <div style={{display:'flex',flexDirection:'column',gap:0}}>

      {/* ── Mes propositions (hors flux flex, dropdown absolu) ── */}
      {isAuthenticated && (
        <div style={{position:'relative',zIndex:50,marginBottom:12}}>
          <button
            onClick={()=>setShowMyPropositions(v=>!v)}
            style={{width:'100%',display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 14px',backgroundColor:'#EFF6FF',border:'1px solid #BFDBFE',borderRadius:showMyPropositions?'10px 10px 0 0':10,cursor:'pointer',gap:8}}
          >
            <div style={{display:'flex',alignItems:'center',gap:7}}>
              <span style={{fontSize:'0.6875rem',fontWeight:700,color:'#1D4ED8',textTransform:'uppercase',letterSpacing:'0.06em'}}>Mes propositions</span>
              {myPropositions.length > 0 && (
                <span style={{backgroundColor:'#DBEAFE',color:'#1D4ED8',fontSize:'0.625rem',fontWeight:700,padding:'1px 6px',borderRadius:9999}}>{myPropositions.length}</span>
              )}
            </div>
            <span style={{fontSize:'0.75rem',color:'#3B82F6',fontWeight:600}}>{showMyPropositions ? '▲ Réduire' : '▼ Voir'}</span>
          </button>
          {showMyPropositions && (
            <div style={{position:'absolute',top:'100%',left:0,right:0,border:'1px solid #BFDBFE',borderTop:'none',borderRadius:'0 0 10px 10px',backgroundColor:'#F8FAFF',padding:'10px 12px',display:'flex',flexDirection:'column',gap:6,boxShadow:'0 8px 24px rgba(0,0,0,0.12)',maxHeight:280,overflowY:'auto'}}>
              {myPropositions.length === 0 ? (
                <div style={{fontSize:'0.8125rem',color:'#9CA3AF',textAlign:'center',padding:'8px 0'}}>Aucune proposition en cours.</div>
              ) : myPropositions.map(s => (
                <div key={s.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,padding:'7px 10px',backgroundColor:'white',borderRadius:7,border:'1px solid #F3F4F6'}}>
                  <div style={{display:'flex',flexDirection:'column',flex:1,minWidth:0}}>
                    <span style={{fontWeight:600,fontSize:'0.8125rem',color:'#111827',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                      {format(new Date(s.date),'EEE d MMM',{locale:fr})}
                    </span>
                    <span style={{fontSize:'0.75rem',color:'#9CA3AF'}}>{s.heure?.substring(0,5)}</span>
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:6,flexShrink:0}}>
                    {s.statut === 'confirmed' ? (
                      <span style={{display:'inline-flex',alignItems:'center',gap:4,backgroundColor:'#D1FAE5',color:'#065F46',borderRadius:6,padding:'3px 9px',fontSize:'0.75rem',fontWeight:700}}>✓ Confirmé</span>
                    ) : (
                      <span style={{backgroundColor:'#FEFCE8',color:'#92400E',borderRadius:6,padding:'3px 9px',fontSize:'0.75rem',fontWeight:700}}>En attente</span>
                    )}
                    <button onClick={()=>handleCancel(s.id)}
                      style={{display:'inline-flex',alignItems:'center',gap:4,padding:'4px 9px',borderRadius:6,border:'1px solid #FECACA',backgroundColor:'#FEF2F2',cursor:'pointer',fontSize:'0.75rem',fontWeight:600,color:'#DC2626'}}>
                      ✕ Annuler
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

    <div className="qso-layout" style={{display:'flex',gap:20,alignItems:'stretch',flexWrap:'wrap'}}>

      {/* ── Prochain QSO ── */}
      <div className="qso-sidebar" style={{width:300,flexShrink:0,display:'flex',flexDirection:'column',gap:12,justifyContent:'space-between'}}>
        <h2 style={{fontSize:'0.75rem',fontWeight:700,color:'#6B7280',textTransform:'uppercase',letterSpacing:'0.08em',margin:0,textAlign:'center'}}>Prochain QSO</h2>

        {mainQSO ? (() => {
          const nom = mainQSO.capitaine_nom_libre || `${mainQSO.capitaine_prenom||''} ${mainQSO.capitaine_nom||''}`.trim();
          const indic = mainQSO.capitaine_indicatif_libre || mainQSO.capitaine_indicatif;
          return (
            <div className="qso-main-card" style={{...S.card,padding:'20px 18px',display:'flex',flexDirection:'column',alignItems:'center',gap:16,textAlign:'center',flex:1}}>
              {/* Icone */}
              <div style={{width:52,height:52,backgroundColor:'#111827',borderRadius:14,display:'flex',alignItems:'center',justifyContent:'center'}}>
                <Radio style={{width:24,height:24,color:'white'}}/>
              </div>
              {/* Date */}
              <div>
                <div style={{fontSize:'1.0625rem',fontWeight:800,color:'#111827',textTransform:'capitalize',lineHeight:1.2}}>
                  {format(new Date(mainQSO.date), "EEEE d MMMM", { locale: fr })}
                </div>
                <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:4,marginTop:4,fontSize:'0.8125rem',color:'#6B7280'}}>
                  <Clock style={{width:12,height:12}}/> {mainQSO.heure?.substring(0,5)}
                </div>
              </div>
              {/* Statut / action */}
              <div style={{width:'100%'}}>
                {mainQSO.statut === 'confirmed' && (
                  <div style={{backgroundColor:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:8,padding:'10px 14px'}}>
                    <div style={{fontSize:'0.6875rem',color:'#6B7280',fontWeight:600,marginBottom:2}}>Capitaine</div>
                    <div style={{fontWeight:800,fontSize:'1rem',color:'#111827'}}>{nom}</div>
                    <div style={{fontFamily:'monospace',fontSize:'0.8125rem',color:'#6B7280',marginTop:2}}>{indic}</div>
                  </div>
                )}
                {mainQSO.statut === 'pending' && (
                  <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:8}}>
                    <span style={{backgroundColor:'#FFFBEB',border:'1px solid #FEF08A',borderRadius:6,padding:'5px 12px',fontSize:'0.8125rem',color:'#854D0E',fontWeight:600}}>En attente de validation</span>
                    {String(mainQSO.capitaine_id) === String(user?.id) && (
                      <button onClick={() => handleCancel(mainQSO.id)} style={{...S.btnGhost,fontSize:'0.75rem'}}>Annuler ma proposition</button>
                    )}
                  </div>
                )}
                {mainQSO.statut === 'libre' && (
                  isAuthenticated ? (
                    <button onClick={() => handlePropose(mainQSO.id)} style={{...S.btnPrimary,width:'100%',justifyContent:'center',padding:'10px'}}>Me proposer comme capitaine</button>
                  ) : proposeForm === mainQSO.id ? (
                    <form onSubmit={e=>{e.preventDefault();handlePropose(mainQSO.id,freeNom,freeIndicatif);}} style={{display:'flex',flexDirection:'column',gap:6}}>
                      <input required placeholder="Votre nom" value={freeNom} onChange={e=>setFreeNom(e.target.value)} style={{padding:'6px 10px',border:'1px solid #D1D5DB',borderRadius:5,fontSize:'0.8125rem',outline:'none'}}/>
                      <input required placeholder="HB9XYZ" value={freeIndicatif} onChange={e=>setFreeIndicatif(e.target.value.toUpperCase())} style={{padding:'6px 10px',border:'1px solid #D1D5DB',borderRadius:5,fontSize:'0.8125rem',fontFamily:'monospace',outline:'none',textTransform:'uppercase'}}/>
                      <div style={{display:'flex',gap:4}}>
                        <button type="submit" style={{...S.btnPrimary,flex:1,justifyContent:'center'}}>OK</button>
                        <button type="button" onClick={()=>setProposeForm(null)} style={{...S.btnOutline,padding:'6px 10px'}}>✕</button>
                      </div>
                    </form>
                  ) : (
                    <button onClick={() => openForm(mainQSO.id)} style={{...S.btnPrimary,width:'100%',justifyContent:'center',padding:'10px'}}>Me proposer comme capitaine</button>
                  )
                )}
              </div>
            </div>
          );
        })() : (
          <div style={{...S.card,padding:20,textAlign:'center',color:'#9CA3AF',fontSize:'0.875rem'}}>Aucune session programmée</div>
        )}

        {/* ── Fréquence + Compte à rebours ── */}
        <div style={{...S.card,padding:'14px 16px',display:'flex',flexDirection:'column',justifyContent:'space-between',flex:1}}>
          <div style={{fontSize:'0.6875rem',fontWeight:700,color:'#6B7280',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:10,textAlign:'center'}}>Fréquence</div>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'7px 10px',backgroundColor:'#F9FAFB',borderRadius:7,marginBottom:12}}>
            <div style={{display:'flex',alignItems:'center',gap:6}}>
              <span style={{fontSize:'0.6rem',fontWeight:700,backgroundColor:'#1E3A8A',color:'white',padding:'2px 5px',borderRadius:4}}>UHF</span>
              <span style={{fontSize:'0.6875rem',fontWeight:600,color:'#6B7280'}}>RU728</span>
            </div>
            <div style={{textAlign:'right'}}>
              <div style={{fontFamily:'monospace',fontSize:'0.8125rem',fontWeight:800,color:'#111827'}}>439.100 <span style={{fontSize:'0.6rem',color:'#9CA3AF'}}>MHz</span></div>
              <div style={{fontSize:'0.6rem',color:'#9CA3AF'}}>FM · ↑431.500</div>
            </div>
          </div>
          <div style={{fontSize:'0.6875rem',fontWeight:700,color:'#6B7280',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:8,textAlign:'center'}}>Prochain QSO dans</div>
          {countdown ? (
            countdown.live ? (
              <div style={{textAlign:'center',padding:'8px',backgroundColor:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:8,fontSize:'0.875rem',fontWeight:700,color:'#15803D'}}>📡 C’est maintenant !</div>
            ) : (
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:4}}>
                {[{v:countdown.days,l:'j'},{v:countdown.hrs,l:'h'},{v:countdown.mins,l:'m'},{v:countdown.secs,l:'s'}].map(({v,l}) => (
                  <div key={l} style={{backgroundColor:'#1E3A8A',borderRadius:8,padding:'8px 4px',textAlign:'center'}}>
                    <div style={{fontFamily:'monospace',fontSize:'1.125rem',fontWeight:800,color:'white',lineHeight:1}}>{String(v).padStart(2,'0')}</div>
                    <div style={{fontSize:'0.5rem',color:'rgba(255,255,255,0.5)',fontWeight:600,textTransform:'uppercase',marginTop:3}}>{l}</div>
                  </div>
                ))}
              </div>
            )
          ) : <div style={{textAlign:'center',color:'#D1D5DB',fontSize:'0.75rem'}}>…</div>}
        </div>

      </div>

      {/* ── Samedis suivants ── */}
      <div style={{flex:1,minWidth:0,width:'100%',display:'flex',flexDirection:'column',gap:12}}>
        <h2 style={{fontSize:'0.75rem',fontWeight:700,color:'#6B7280',textTransform:'uppercase',letterSpacing:'0.08em',margin:0,textAlign:'center'}}>Samedis suivants</h2>

        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {futureQSO.map(item => {
            const isMine = user && String(item.capitaine_id) === String(user.id);
            const capNom = item.capitaine_nom_libre || `${item.capitaine_prenom||''} ${item.capitaine_nom||''}`.trim();
            const capIndic = item.capitaine_indicatif_libre || item.capitaine_indicatif;
            return (
              <div key={item.id} className="qso-card-row" style={{...S.card,padding:'14px 16px',display:'flex',alignItems:'center',gap:14}}>
                {/* Mini calendrier */}
                <div style={{width:40,textAlign:'center',flexShrink:0}}>
                  <div style={{fontSize:'0.625rem',fontWeight:700,color:'#9CA3AF',textTransform:'uppercase',letterSpacing:'0.05em'}}>
                    {format(new Date(item.date),'MMM',{locale:fr})}
                  </div>
                  <div style={{fontSize:'1.25rem',fontWeight:800,color:'#111827',lineHeight:1.1}}>
                    {format(new Date(item.date),'dd',{locale:fr})}
                  </div>
                </div>

                <div style={{flex:1}}>
                  <div style={{fontWeight:600,fontSize:'0.875rem',color:'#111827'}}>QSO de Section</div>
                  <div style={{fontSize:'0.75rem',color:'#9CA3AF',display:'flex',alignItems:'center',gap:4,marginTop:1}}>
                    <Clock style={{width:10,height:10}}/>{item.heure?.substring(0,5)}
                  </div>
                </div>

                {/* Action */}
                <div style={{flexShrink:0}}>
                  {(item.statut === 'confirmed' || item.statut === 'pending') && capNom && (
                    <div style={{textAlign:'right'}}>
                      <div style={{fontSize:'0.6875rem',color:'#9CA3AF',marginBottom:1}}>
                        {item.statut === 'confirmed' ? 'Capitaine' : 'En attente'}
                      </div>
                      <div style={{fontWeight:700,fontSize:'0.8125rem',color: item.statut === 'confirmed' ? '#111827' : '#92400E'}}>{capNom}</div>
                      <div style={{fontFamily:'monospace',fontSize:'0.6875rem',color:'#9CA3AF',letterSpacing:'0.05em'}}>{capIndic}</div>
                      {item.statut === 'pending' && isMine && (
                        <button onClick={() => handleCancel(item.id)} style={{...S.btnGhost,marginTop:3,fontSize:'0.6875rem'}}>Annuler</button>
                      )}
                    </div>
                  )}
                  {item.statut === 'libre' && (
                    isAuthenticated ? (
                      <button onClick={() => handlePropose(item.id)} style={S.btnOutline}>Me proposer</button>
                    ) : proposeForm === item.id ? (
                      <form onSubmit={e=>{e.preventDefault();handlePropose(item.id,freeNom,freeIndicatif);}} style={{display:'flex',flexDirection:'column',gap:6,minWidth:180}}>
                        <input required placeholder="Nom" value={freeNom} onChange={e=>setFreeNom(e.target.value)} style={{padding:'5px 8px',border:'1px solid #D1D5DB',borderRadius:5,fontSize:'0.75rem',outline:'none'}}/>
                        <input required placeholder="HB9XYZ" value={freeIndicatif} onChange={e=>setFreeIndicatif(e.target.value.toUpperCase())} style={{padding:'5px 8px',border:'1px solid #D1D5DB',borderRadius:5,fontSize:'0.75rem',fontFamily:'monospace',outline:'none',textTransform:'uppercase'}}/>
                        <div style={{display:'flex',gap:4}}>
                          <button type="submit" style={{...S.btnPrimary,flex:1,padding:'5px 8px',fontSize:'0.75rem'}}>OK</button>
                          <button type="button" onClick={()=>setProposeForm(null)} style={{...S.btnOutline,padding:'5px 8px',fontSize:'0.75rem'}}>✕</button>
                        </div>
                      </form>
                    ) : (
                      <button onClick={() => openForm(item.id)} style={S.btnOutline}>Me proposer</button>
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Carte météo + propagation ── */}
      <div className="qso-sidebar" style={{width:300,flexShrink:0,display:'flex',flexDirection:'column',gap:12}}>
        <h2 style={{fontSize:'0.75rem',fontWeight:700,color:'#6B7280',textTransform:'uppercase',letterSpacing:'0.08em',margin:0,textAlign:'center'}}>Conditions</h2>
        <div style={{...S.card,padding:'20px 18px',display:'flex',flexDirection:'column',gap:0,flex:1,justifyContent:'space-between'}}>

          {/* Texte fédérateur */}
          <div style={{textAlign:'center',paddingBottom:20}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8,marginBottom:6}}>
              <div style={{width:28,height:28,backgroundColor:'#111827',borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                <Radio style={{width:14,height:14,color:'white'}}/>
              </div>
              <span style={{fontSize:'1rem',fontWeight:800,color:'#111827',letterSpacing:'-0.01em'}}>QSO de Section</span>
            </div>
            <div style={{display:'inline-flex',alignItems:'center',gap:5,backgroundColor:'#F3F4F6',borderRadius:20,padding:'3px 10px',marginBottom:12}}>
              <Clock style={{width:10,height:10,color:'#6B7280'}}/>
              <span style={{fontSize:'0.6875rem',fontWeight:700,color:'#374151',textTransform:'uppercase',letterSpacing:'0.06em'}}>Chaque samedi · 11h00</span>
            </div>
            <p style={{margin:'0 0 8px',fontSize:'0.875rem',color:'#111827',lineHeight:1.75}}>
              Le QSO de section est le <strong>rendez-vous hebdomadaire</strong> des membres HB9G. Un moment d'échange convivial, ouvert à tous les radioamateurs — membres ou visiteurs.
            </p>
            <p style={{margin:'0 0 0',fontSize:'0.875rem',color:'#374151',lineHeight:1.65}}>
              Venez partager vos activités, poser vos questions et rester connectés à la vie du club.
            </p>
          </div>

          <div style={{borderTop:'1px solid #F3F4F6',paddingTop:16}}>

          {/* 2 colonnes : météo | propagation */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>

            {/* Météo */}
            <div style={{display:'flex',flexDirection:'column',gap:6}}>
              <div style={{fontSize:'0.6875rem',fontWeight:700,color:'#9CA3AF',textTransform:'uppercase',letterSpacing:'0.06em'}}>Météo · Genève</div>
              {weather ? (<>
                <div style={{display:'flex',alignItems:'center',gap:6}}>
                  <span style={{fontSize:'1.75rem',lineHeight:1}}>{weatherIcon(weather.code)}</span>
                  <div>
                    <div style={{fontSize:'1.375rem',fontWeight:800,color:'#111827',lineHeight:1}}>{weather.temp}°</div>
                    <div style={{fontSize:'0.625rem',color:'#9CA3AF'}}>res. {weather.feels}°</div>
                  </div>
                </div>
                <div style={{fontSize:'0.75rem',color:'#374151',fontWeight:500,lineHeight:1.3}}>{weather.desc}</div>
                <div style={{fontSize:'0.625rem',color:'#9CA3AF',display:'flex',flexDirection:'column',gap:2,marginTop:2}}>
                  <span>💧 {weather.humidity}%</span>
                  <span>💨 {weather.wind} km/h</span>
                </div>
              </>) : <div style={{color:'#D1D5DB',fontSize:'0.75rem'}}>…</div>}
            </div>

            {/* Propagation */}
            <div style={{display:'flex',flexDirection:'column',gap:6,borderLeft:'1px solid #F3F4F6',paddingLeft:12}}>
              <div style={{fontSize:'0.6875rem',fontWeight:700,color:'#9CA3AF',textTransform:'uppercase',letterSpacing:'0.06em'}}>Propagation HF</div>
              {solar ? (() => {
                const p = propLevel(solar.k, solar.sfi);
                return (<>
                  {/* Barres de niveau */}
                  <div style={{display:'flex',alignItems:'flex-end',gap:3,height:28}}>
                    {[1,2,3,4].map(i => (
                      <div key={i} style={{
                        flex:1, borderRadius:3,
                        height: `${i * 25}%`,
                        backgroundColor: i <= p.bars ? p.color : '#E5E7EB',
                        transition:'background 0.3s'
                      }}/>
                    ))}
                  </div>
                  {/* Badge niveau */}
                  <div style={{display:'inline-flex',alignItems:'center',gap:4,padding:'3px 8px',borderRadius:6,background:p.bg,border:`1px solid ${p.border}`,alignSelf:'flex-start'}}>
                    <span style={{fontSize:'0.6875rem',fontWeight:700,color:p.color}}>{p.label}</span>
                  </div>
                  {/* Détails */}
                  <div style={{fontSize:'0.625rem',color:'#9CA3AF',display:'flex',flexDirection:'column',gap:1,marginTop:2}}>
                    <span>SFI · {solar.sfi}</span>
                    <span>K · {solar.k} — {kLabel(solar.k)}</span>
                  </div>
                </>);
              })() : <div style={{color:'#D1D5DB',fontSize:'0.75rem'}}>…</div>}
            </div>

          </div>
          </div>
        </div>
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)}/>}
    </div>
    </div>
  );
};

export default QSOPage;
